import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '~/assets/icons';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import deviceApi from '~/apis/device';
import locationApi from '~/apis/location';
import { RoleIdUserEnum } from '~/utils/enum';
import { handleApiError, notify } from '~/utils/helper';
import { IMasterDataItem } from '~/utils/interface/master';

type DeviceRow = {
  imei?: string;
  status?: number | string;
  province_id?: number | string;
  province_code?: number | string;
  ward_id?: number | string;
  ward_code?: number | string;
  note?: string;
};

type UpdateDeviceDialogProps = {
  open: boolean;
  device: DeviceRow | null;
  currentUser: any;
  onClose: () => void;
  onUpdated: () => void;
};

const getDeviceStatusValue = (status?: number | string) => {
  const normalizedStatus = Number(status);
  return normalizedStatus === 2 ? '2' : '1';
};

const normalizeOptionalId = (value?: number | string) => {
  if (value === undefined || value === null || value === '' || value === '0' || value === 0) {
    return '';
  }

  return String(value);
};

const UpdateDeviceDialog = ({ open, device, currentUser, onClose, onUpdated }: UpdateDeviceDialogProps) => {
  const { t } = useTranslation('admin/device');
  const [provinceOptions, setProvinceOptions] = useState<IMasterDataItem[]>([]);
  const [wardOptions, setWardOptions] = useState<IMasterDataItem[]>([]);
  const [status, setStatus] = useState('1');
  const [provinceId, setProvinceId] = useState('');
  const [wardId, setWardId] = useState('');
  const [note, setNote] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAdmin = Number(currentUser?.roleid) === RoleIdUserEnum.QUAN_TRI;
  const isProvinceRole = Number(currentUser?.roleid) === RoleIdUserEnum.TINH_THANH;

  useEffect(() => {
    if (!open || !device) return;

    setStatus(getDeviceStatusValue(device.status));
    setProvinceId(normalizeOptionalId(device.province_id ?? device.province_code ?? currentUser?.province_id));
    setWardId(normalizeOptionalId(device.ward_id ?? device.ward_code));
    setNote(device.note ?? '');

    const loadProvinces = async () => {
      try {
        const response = await locationApi.getListProvinces();
        setProvinceOptions(
          response.data.map((item) => ({
            id: item.code,
            name: item.name,
            data0: item.name,
            data1: item.name_with_type,
            data2: item.type,
          }))
        );
      } catch (error) {
        handleApiError(error);
      }
    };

    loadProvinces();
  }, [open, device, currentUser?.province_id]);

  useEffect(() => {
    if (!open || !provinceId) {
      setWardOptions([]);
      return;
    }

    const loadWards = async () => {
      try {
        setIsLoading(true);
        const response = await locationApi.getListWards({ province: provinceId });
        setWardOptions(
          response.data.map((item) => ({
            id: item.code,
            name: item.name,
            data0: item.name,
            data1: item.name_with_type,
            data2: item.type,
            data3: item.parent_code,
          }))
        );
      } catch (error) {
        setWardOptions([]);
        handleApiError(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadWards();
  }, [open, provinceId]);

  const handleSubmit = async () => {
    if (!device?.imei) {
      notify('error', t('updateDeviceDialog.required'));
      return;
    }

    try {
      setIsSubmitting(true);
      const safeProvinceId = normalizeOptionalId(provinceId);
      const safeWardId = normalizeOptionalId(wardId);
      const payload = {
        imei: device.imei,
        status,
        note,
        ...(safeProvinceId ? { province_id: safeProvinceId } : {}),
        ...(safeWardId ? { ward_id: safeWardId } : {}),
      };
      await deviceApi.updateDeviceInfo(payload);
      notify('success', t('updateDeviceDialog.success'));
      onClose();
      onUpdated();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} fullWidth maxWidth='sm'>
      <DialogTitle className='flex items-center justify-between bg-[#123B6D] text-white'>
        <Box>
          <Typography className='text-xl font-semibold text-white'>{t('updateDeviceDialog.title')}</Typography>
          {/* <Typography className='text-sm text-white/80'>{device?.imei}</Typography> */}
        </Box>
        <IconButton onClick={onClose} disabled={isSubmitting} className='text-white'>
          <CloseIcon className='h-5 w-5' />
        </IconButton>
      </DialogTitle>

      <DialogContent className='flex flex-col gap-4 pt-6 mt-4'>
        <Box>
          <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>{t('updateDeviceDialog.imei')}</Typography>
          <Box className='flex h-12 items-center rounded-xl border border-[#D7DEE7] bg-[#D7DEE7] px-4 text-[#7C8796]'>
            {device?.imei}
          </Box>
        </Box>

        <Box>
          <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>{t('status.title')}</Typography>
          <BaseSelect
            options={[
              { id: '1', name: t('status.normal') },
              { id: '2', name: t('status.broken') },
            ]}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            isSortEnable={false}
          />
        </Box>

        <Box>
          <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>{t('province_id')}</Typography>
          <BaseSelect
            options={isAdmin ? provinceOptions : provinceOptions.filter((item) => String(item.id) === provinceId)}
            value={provinceId}
            placeholder={t('select_province_id')}
            onChange={(event) => {
              setProvinceId(event.target.value);
              setWardId('');
            }}
            disabled={!isAdmin || isProvinceRole}
            isDisplayAllOption={false}
            isSearchEnable
          />
        </Box>

        <Box>
          <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>{t('ward_id')}</Typography>
          <BaseSelect
            options={wardOptions}
            value={wardId}
            placeholder={t('select_ward_id')}
            onChange={(event) => setWardId(event.target.value)}
            disabled={!isAdmin && !isProvinceRole}
            isSearchEnable
          />
        </Box>

        <Box>
          <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>{t('updateDeviceDialog.note')}</Typography>
          <TextField
            value={note}
            onChange={(event) => setNote(event.target.value)}
            
            minRows={3}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                minHeight: 48,
                padding: 0,
                borderRadius: '12px',
                backgroundColor: '#FFFFFF',
              },
              '& .MuiInputBase-inputMultiline': {
                padding: '12px 18px',
                color: '#161A1F',
                fontSize: '16px',
              },
              '& .MuiInputBase-inputMultiline::placeholder': {
                color: '#7C8796',
                opacity: 1,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#D7DEE7',
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#D7DEE7',
              },
            }}
          />
        </Box>

        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={isSubmitting || isLoading}
          className='bg-[#123B6D] hover:bg-[#0E2F58]'
        >
          {isSubmitting ? <CircularProgress size={22} color='inherit' /> : t('updateDeviceDialog.save')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDeviceDialog;